import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';

export interface ParserConfig {
  id: string;
  workspace_id: string;
  primary_parser: 'mistral' | 'openai' | 'openrouter' | 'deepseek';
  fallback_parsers: string[];
  temperature: number;
  max_tokens: number;
  timeout_seconds: number;
  created_at: string;
  updated_at: string;
}

export interface UpdateParserConfigData {
  primary_parser?: 'mistral' | 'openai' | 'openrouter' | 'deepseek';
  fallback_parsers?: string[];
  temperature?: number;
  max_tokens?: number;
  timeout_seconds?: number;
}

export const useParserConfig = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const configQuery = useQuery({
    queryKey: ['parser-config', user?.id],
    queryFn: async () => {
      // For MVP, return default config (later will fetch from database)
      return {
        id: 'default',
        workspace_id: user?.id || '',
        primary_parser: 'openai' as const,
        fallback_parsers: ['mistral', 'openrouter'],
        temperature: 0.1,
        max_tokens: 4000,
        timeout_seconds: 120,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
    },
    enabled: !!user?.id,
  });

  const updateConfigMutation = useMutation({
    mutationFn: async (data: UpdateParserConfigData) => {
      // For MVP, just update locally (later will update database)
      return {
        ...configQuery.data!,
        ...data,
        updated_at: new Date().toISOString(),
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parser-config'] });
      toast({
        title: "Configuration saved",
        description: "Parser settings have been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save configuration",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    config: configQuery.data,
    isLoading: configQuery.isLoading,
    error: configQuery.error,
    updateConfig: updateConfigMutation.mutateAsync,
    isUpdating: updateConfigMutation.isPending,
  };
};
